import { useState, useCallback } from 'react'
import { useAuth } from '../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface UseReformatOptions {
  documentId: string
  onSuccess?: () => void
  onError?: (error: string) => void
}

export function useReformat({ documentId, onSuccess, onError }: UseReformatOptions) {
  const [isReformatting, setIsReformatting] = useState(false)
  const [reformatError, setReformatError] = useState<string | null>(null)
  const { firebaseUser } = useAuth()

  const reformat = useCallback(async (transcriptText: string) => {
    if (!firebaseUser || !transcriptText.trim()) {
      return
    }

    try {
      setIsReformatting(true)
      setReformatError(null)

      console.log('🔄 Starting reformat:', { documentId, textLength: transcriptText.length })

      // まず文字起こしを更新
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const updateResponse = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcript: {
            text: transcriptText,
            source: 'manual_edit',
            lang: 'ja-JP'
          }
        })
      })

      if (!updateResponse.ok) {
        throw new Error(`文字起こしの更新に失敗しました: ${updateResponse.statusText}`)
      }

      console.log('✅ Transcript updated, starting format...')

      // AI整形を実行
      const formatResponse = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${documentId}/format`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!formatResponse.ok) {
        if (formatResponse.status === 429) {
          throw new Error('AI整形サービスが混雑しています。しばらく時間をおいてから再度お試しください。')
        }
        throw new Error(`AI整形に失敗しました: ${formatResponse.statusText}`)
      }

      const formatResult = await formatResponse.json()
      console.log('✅ Reformat completed:', formatResult)

      onSuccess?.()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '再成形に失敗しました'
      console.error('❌ Reformat failed:', error)
      setReformatError(errorMessage)
      onError?.(errorMessage)
    } finally {
      setIsReformatting(false)
    }
  }, [documentId, firebaseUser, onSuccess, onError])

  return {
    reformat,
    isReformatting,
    reformatError
  }
}
